import React from "react";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Chip,
  Paper,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";

// Marketeers brand palette
const MK_NAVY = "#08306B";
const MK_RED = "#CD393B";
const MK_GREY = "#929292";

const dummyFiles = [
  {
    id: 1,
    fileName: "Category A – Wave 1",
    fileType: "Excel",
    noItems: 120,
    noBrands: 8,
    noSegments: 4,
    lastUpdate: "2025-11-20",
  },
  {
    id: 2,
    fileName: "Category B – Tracker",
    fileType: "CSV",
    noItems: 80,
    noBrands: 5,
    noSegments: 3,
    lastUpdate: "2025-11-18",
  },
];

export default function ProjectStructurePage() {
  const handleUpload = (event) =>
    console.log("Uploaded files:", event.target.files);
  const handleLaunch = () => navigate("/dashboard");
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        backgroundColor: "#F4F6FB",
        minHeight: "100vh",
        py: 4,
        px: 2,
      }}
    >
      <Container maxWidth="xl">
        {/* Last update strip */}
        <Box
          display="flex"
          justifyContent="flex-end"
          color={MK_GREY}
          fontSize={16}
          mb={2}
        >
          Last Data Update:&nbsp;
          <Box component="span" fontWeight="600" color={MK_NAVY}>
            20 November 2025
          </Box>
        </Box>

        {/* Center wrapper */}
        <Box display="flex" justifyContent="center" width="100%">
          <Box sx={{ width: "100%", maxWidth: "1400px" }}>
            {/* Main white card */}
            <Paper
              elevation={3}
              sx={{
                borderRadius: 4,
                px: { xs: 2, md: 4 },
                py: { xs: 2, md: 3 },
              }}
            >
              {/* Page title */}
              <Box
                mb={2}
                display="flex"
                alignItems="center"
                justifyContent="center"
                position="relative"
                >
                {/* BACK BUTTON */}
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate("/select-user-id")}
                    sx={{
                    position: "absolute",
                    left: 0,
                    color: MK_NAVY,
                    fontWeight: 600,
                    textTransform: "none",
                    "&:hover": { backgroundColor: "rgba(8,48,107,0.08)" },
                    }}
                >
                    Back
                </Button>

                {/* TITLE */}
                <Typography
                    variant="subtitle1"
                    fontWeight="600"
                    sx={{ color: MK_NAVY, letterSpacing: 0.5 }}
                >
                    Savola – Oils &amp; Ghee | Market Overview Project
                </Typography>
                </Box>

              {/* Quick tags */}
              

              {/* Layout: Left (summary + checks + table) / Right (details + launch) */}
              <Grid container spacing={2} alignItems="stretch">
                {/* LEFT COLUMN */}
                <Grid item xs={12} md={9}>
                  {/* Project Summary */}
                  <Card
                    sx={{
                      borderRadius: 3,
                      mb: 2,
                      border: "1px solid #EEF0F7",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="subtitle2"
                        fontWeight="600"
                        sx={{ color: MK_NAVY, mb: 1 }}
                      >
                        Project Summary
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{ color: "#555", mb: 0.5 }}
                      >
                        • Track Savola performance vs. total Oils &amp; Ghee
                        market.
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#555", mb: 0.5 }}
                      >
                        • Aggregate sales, share &amp; pricing to feed Strategy
                        Creation and Decision Center.
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#555" }}>
                        • Use structures to control brand, SKU and segment
                        mapping into the engine.
                      </Typography>
                    </CardContent>
                  </Card>

                  {/* Data Quality Checks */}
                  <Card
                    sx={{
                      borderRadius: 3,
                      mb: 2,
                      border: "1px solid #EEF0F7",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="subtitle2"
                        fontWeight="600"
                        sx={{ color: MK_NAVY, mb: 1 }}
                      >
                        Data Quality Checks
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.2,
                          color: "#444",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="body2">
                            Structure uploaded
                          </Typography>
                          <Chip
                            label="Passed"
                            size="small"
                            sx={{
                              backgroundColor: "rgba(13,166,90,0.1)",
                              color: "#0DA65A",
                              borderRadius: 999,
                            }}
                          />
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="body2">
                            Brand mapping
                          </Typography>
                          <Chip
                            label="Passed"
                            size="small"
                            sx={{
                              backgroundColor: "rgba(13,166,90,0.1)",
                              color: "#0DA65A",
                              borderRadius: 999,
                            }}
                          />
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="body2">
                            Segment alignment
                          </Typography>
                          <Chip
                            label="Missing"
                            size="small"
                            sx={{
                              backgroundColor: "rgba(205,57,59,0.1)",
                              color: MK_RED,
                              borderRadius: 999,
                            }}
                          />
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="body2">
                            Value / Volume consistency
                          </Typography>
                          <Chip
                            label="Passed"
                            size="small"
                            sx={{
                              backgroundColor: "rgba(13,166,90,0.1)",
                              color: "#0DA65A",
                              borderRadius: 999,
                            }}
                          />
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="body2">
                            Outlier detection
                          </Typography>
                          <Chip
                            label="Passed"
                            size="small"
                            sx={{
                              backgroundColor: "rgba(13,166,90,0.1)",
                              color: "#0DA65A",
                              borderRadius: 999,
                            }}
                          />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Structures table */}
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      p: 2,
                      border: "1px solid #EEF0F7",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight="600"
                        sx={{ color: MK_NAVY }}
                      >
                        Uploaded Structures
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: MK_GREY }}
                      >
                        {dummyFiles.length} file(s)
                      </Typography>
                    </Box>

                    <Divider sx={{ mb: 1 }} />

                    <Box sx={{ width: "100%", overflowX: "auto" }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow
                            sx={{
                              "& th": {
                                backgroundColor: "#FFFFFF",
                                color: MK_NAVY,
                                fontSize: 11,
                                fontWeight: 600,
                                textTransform: "uppercase",
                                borderBottom: "1px solid #EEF0F7",
                              },
                            }}
                          >
                            <TableCell>#</TableCell>
                            <TableCell>File Name</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Items</TableCell>
                            <TableCell>Brands</TableCell>
                            <TableCell>Segments</TableCell>
                            <TableCell>Last Update</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dummyFiles.map((row) => (
                            <TableRow
                              key={row.id}
                              hover
                              sx={{
                                "& td": {
                                  borderBottom: "1px solid #F3F4FA",
                                },
                                "&:hover": {
                                  backgroundColor: "rgba(8,48,107,0.03)",
                                },
                              }}
                            >
                              <TableCell>{row.id}</TableCell>
                              <TableCell>{row.fileName}</TableCell>
                              <TableCell>
                                <Chip
                                  label={row.fileType}
                                  size="small"
                                  sx={{
                                    backgroundColor: "rgba(8,48,107,0.05)",
                                    color: MK_NAVY,
                                    borderRadius: 999,
                                    fontWeight: 500,
                                  }}
                                />
                              </TableCell>
                              <TableCell>{row.noItems}</TableCell>
                              <TableCell>{row.noBrands}</TableCell>
                              <TableCell>{row.noSegments}</TableCell>
                              <TableCell>{row.lastUpdate}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>

                    {/* Upload button */}
                    <Box mt={2}>
                      <Button
                        variant="outlined"
                        fullWidth
                        component="label"
                        sx={{
                          py: 1.4,
                          borderRadius: 999,
                          borderColor: MK_NAVY,
                          color: "#ffff",
                          backgroundColor: MK_NAVY,
                          fontWeight: 600,
                          fontSize: 13,
                          textTransform: "uppercase",
                          "&:hover": {
                            borderColor: MK_NAVY,
                            backgroundColor: "rgba(8,48,107,0.06)",
                            color: MK_NAVY
                            },
                        }}
                      >
                        Upload new file
                        <input
                          hidden
                          type="file"
                          multiple
                          onChange={handleUpload}
                        />
                      </Button>
                    </Box>
                  </Paper>
                </Grid>

                {/* RIGHT COLUMN */}
                <Grid item xs={12} md={3}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      height: "100%",
                    }}
                  >
                    {/* Project Details */}
                    <Card
                      sx={{
                        borderRadius: 3,
                        border: "1px solid #EEF0F7",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                      }}
                    >
                      <CardContent>
                        <Typography
                          variant="subtitle2"
                          fontWeight="600"
                          sx={{ color: MK_NAVY, mb: 1 }}
                        >
                          Project Details
                        </Typography>

                        <Box
                          component="dl"
                          sx={{
                            m: 0,
                            "& dt": {
                              fontSize: 11,
                              textTransform: "uppercase",
                              color: MK_GREY,
                              mb: 0.25,
                            },
                            "& dd": {
                              m: 0,
                              mb: 1,
                              fontSize: 13,
                              color: "#333",
                              fontWeight: 500,
                            },
                          }}
                        >
                          <dt>Client</dt>
                          <dd>Savola</dd>

                          <dt>Market</dt>
                          <dd>Saudi Arabia</dd>

                          <dt>Coverage</dt>
                          <dd>Modern Trade + Key Accounts</dd>

                          <dt>Data Source</dt>
                          <dd>Retail Scan</dd>

                          <dt>Owner</dt>
                          <dd>Mahmoud / Marketeers</dd>

                          <dt>Status</dt>
                          <dd>
                            <Chip
                              label="Active"
                              size="small"
                              sx={{
                                backgroundColor: "rgba(13,166,90,0.1)",
                                color: "#0DA65A",
                                borderRadius: 999,
                              }}
                            />
                          </dd>
                        </Box>
                      </CardContent>
                    </Card>

                    {/* Ready to Launch */}
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        p: 2,
                        border: "1px solid #EEF0F7",
                        boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                        background:
                          "linear-gradient(145deg, #08306B 0%, #002152 100%)",
                        color: "#ffffff",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                        flexGrow: 1,
                      }}
                    >
                      <Box textAlign="center">
                        <Typography variant="subtitle2" fontWeight="600">
                          Ready to launch?
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ opacity: 0.85, mt: 0.5 }}
                        >
                          Run the analytics engine using the latest structures
                          and mappings.
                        </Typography>
                      </Box>

                      <Button
                        fullWidth
                        variant="contained"
                        sx={{
                          py: 1.2,
                          backgroundColor: "#FFFFFF",
                          color: MK_NAVY,
                          borderRadius: 999,
                          fontWeight: 700,
                          fontSize: 13,
                          textTransform: "uppercase",
                          "&:hover": { backgroundColor: "#F4F4F4" },
                        }}
                        onClick={handleLaunch}
                      >
                        Launch
                      </Button>
                    </Paper>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
